import React, { useEffect, useRef, useState } from "react";
import { svg } from "../../../Assets/svg";
import { Button, Dropdown, Menu, Spin, Table, TableColumnsType } from "antd";
import { RootState, useAppDispatch, useAppSelector } from "../../../store";
import { getAntdSortOrder, useDebounce } from "../../../helpers/utils";
import NotificationButton from "../../Components/NotificationButton";
import FullScreenLoader from "../../Components/FullScreenLoader";
import NoDataMessage from "../../Components/NoData";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  deleteProductTypeAPI,
  getProductTypeListAPI,
  reorderProductTypeAPI,
} from "./productTypeApis";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../helpers/permissionModule";
import NoPermissionPage from "../../Components/NoPermissionPage";
import CreateProductTypeModal from "./Components/Modals/CreateProductTypeModal";
import {
  AdditionalProductFieldsEnum,
  AdditionalProductFieldsLabels,
  PriceCalculationEnum,
  PriceCalculationLabels,
} from "../Products/productsTypes";
import { CommonConfirmationModal } from "../../../Components/CommonConfirmationModal";

const DragHandle = (props: any) => {
  const { setActivatorNodeRef, listeners } = useSortable({ id: props.id });
  return (
    <div
      ref={setActivatorNodeRef}
      {...listeners}
      className="cursor-grab p-2 svg-24"
      style={{
        touchAction: "none",
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        userSelect: "none",
      }}
    >
      {svg?.dragHandleDots || "⋮⋮"}
    </div>
  );
};

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  "data-row-key": string;
}

const Row = (props: RowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props["data-row-key"],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(
      transform && { ...transform, scaleY: 1 },
    )?.replace(
      /translate3d\(([^,]+),([^,]+),([^)]+)\)/,
      "translate3d($1, $2, 0)",
    ),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
  };

  return (
    <tr
      {...props}
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`${props.className || ""} ${
        isDragging ? "bg-light opacity-75 shadow-sm" : ""
      }`}
    />
  );
};

const ProductTypes = () => {
  const dispatch = useAppDispatch();

  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData?.MASTER_DATA,
  );
  const { authLoading } = useAppSelector((state: RootState) => state?.loading);
  const [reordering, setReordering] = useState<boolean>(false);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [reorderItemsList, setReorderItemsList] = useState<any[]>([]);
  const [productTypes, setProductTypes] = useState<any[]>([]);

  const abortControllerRef = useRef<AbortController | null>(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 991.98);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 991.98);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit] = useState<number>(30);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState<boolean>(false);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [selectedProductType, setSelectedProductType] = useState(null);

  const [idToDelete, setIdToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [sorterState, setSorterState] = useState<{
    field: string | null;
    order: "asc" | "desc" | null;
  }>({ field: null, order: null });

  // Search
  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search, 300);

  const fetchProductTypes = (page: number, isReset: boolean) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (isReset) {
      setLoading(true);
    }

    const filters = {
      page: page,
      limit: limit,
      search: debouncedSearch,
      sortBy: sorterState.field,
      sortOrder: sorterState.order,
    };

    dispatch(
      getProductTypeListAPI({
        filters,
        signal: controller.signal,
        onSuccess: (data: any) => {
          const newData = data?.data || [];
          const total = data?.count || 0;
          setTotalCount(total);

          if (isReset) {
            setProductTypes(newData);
            window.scrollTo(0, 0);
          } else {
            setProductTypes((prev) => [...prev, ...newData]);
          }

          const currentCount = isReset
            ? newData.length
            : productTypes.length + newData.length;
          setHasMore(currentCount < total);
          setLoading(false);
        },
        onError: () => {
          setLoading(false);
        },
      }),
    );
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    const activeIndex = productTypes?.findIndex((i) => i._id === active.id);
    const overIndex = productTypes?.findIndex((i) => i._id === over.id);

    if (activeIndex === -1 || overIndex === -1) {
      return;
    }

    const reordered = arrayMove(productTypes, activeIndex, overIndex);
    setProductTypes(reordered);

    handleSaveOrder(active.id as string, activeIndex, overIndex);
  };

  const handleSaveOrder = (
    draggedId: string,
    oldIndex: number,
    newIndex: number,
  ) => {
    setReordering(true);
    const payload = { id: draggedId, order: newIndex };
    dispatch(
      reorderProductTypeAPI({
        payload,
        onSuccess: () => {
          setReordering(false);
        },
        onError: () => {
          setReordering(false);
          fetchProductTypes(1, true); // Revert on error
        },
      }),
    );
  };

  // --- MENUS ---
  const headerMoreMenu = (
    <Menu>
      {USER_PERMISSIONS?.MODIFY && (
        <Menu.Item
          key="1"
          onClick={() => {
            setSearch("");
            setSorterState({ field: null, order: null });
            setIsReorderMode(true);
          }}
        >
          Reorder
        </Menu.Item>
      )}
    </Menu>
  );

  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    fetchProductTypes(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, sorterState]);

  const handleLoadMore = () => {
    console.log("Handle load more");
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchProductTypes(nextPage, false);
  };

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    const order =
      sorter.order === "ascend"
        ? "asc"
        : sorter.order === "descend"
        ? "desc"
        : null;
    setSorterState({ field: sorter.field, order: order as any });
  };

  const handleEditClick = (record: any) => {
    setSelectedProductType(record);
    setIsCreateModalOpen(true);
  };

  const MoreOptionsMenu = (data: any) => {
    return (
      <Menu>
        {USER_PERMISSIONS.MODIFY && (
          <Menu.Item key="1" onClick={() => handleEditClick(data)}>
            <div className="text-black d-flex align-items-center gap-2 svg-20">
              Modify
            </div>
          </Menu.Item>
        )}

        {/* ✅ Remove Guard */}
        {USER_PERMISSIONS.REMOVE && !data?.is_default && (
          <Menu.Item key="2" onClick={() => setIdToDelete(data._id)}>
            <div className="text-danger d-flex align-items-center gap-2 svg-20">
              Remove
            </div>
          </Menu.Item>
        )}
      </Menu>
    );
  };

  const columns: TableColumnsType<any> = [
    ...(isReorderMode
      ? [
          {
            title: "",
            key: "sort",
            width: "1%",
            render: (_: any, record: any) => <DragHandle id={record._id} />,
          },
        ]
      : []),
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: isReorderMode ? false : true,
      sortOrder: getAntdSortOrder("name", sorterState),
      render: (name: string, record: any) => (
        <div className="d-flex align-items-center">{name}</div>
      ),
    },
    {
      title: "Additional Field",
      dataIndex: "load_key",
      key: "load_key",
      sorter: isReorderMode ? false : true,
      sortOrder: getAntdSortOrder("load_key", sorterState),
      render: (load_key: string, record: any) => (
        <div className="d-flex align-items-center">
          {load_key
            ? AdditionalProductFieldsLabels[
                load_key as AdditionalProductFieldsEnum
              ]
            : "-"}
        </div>
      ),
    },
    {
      title: "Mark Up",
      dataIndex: "mark_up",
      key: "mark_up",
      sorter: isReorderMode ? false : true,
      sortOrder: getAntdSortOrder("mark_up", sorterState),
      render: (mark_up: number, record: any) => (
        <div className="d-flex align-items-center">
          {mark_up ? `${mark_up}%` : "0%"}
        </div>
      ),
    },
    {
      title: "Installation Mark Up",
      dataIndex: "installation_mark_up",
      key: "installation_mark_up",
      sorter: isReorderMode ? false : true,
      sortOrder: getAntdSortOrder("installation_mark_up", sorterState),
      render: (installation_mark_up: number) => (
        <div className="d-flex align-items-center">
          {installation_mark_up ? `${installation_mark_up}%` : "0%"}
        </div>
      ),
    },
    {
      title: "Necessary",
      dataIndex: "quote_product_is_required",
      key: "quote_product_is_required",
      sorter: isReorderMode ? false : true,
      sortOrder: getAntdSortOrder("quote_product_is_required", sorterState),
      render: (quote_product_is_required: number) => (
        <div className="d-flex align-items-center">
          {quote_product_is_required ? "Yes" : "No"}
        </div>
      ),
    },
    {
      title: "Price Calculation",
      dataIndex: "price_calculation",
      key: "price_calculation",
      sorter: isReorderMode ? false : true,
      sortOrder: getAntdSortOrder("price_calculation", sorterState),
      render: (price_calculation: string | boolean) => {
        let val = price_calculation;
        if (val === true) val = PriceCalculationEnum.CALCULATION;
        if (val === false || !val) val = PriceCalculationEnum.NOT_SET;

        if (val === PriceCalculationEnum.CALCULATION) {
          return (
            <div className="d-flex align-items-center">
              <span className="text-success fw-semibold">
                {PriceCalculationLabels[PriceCalculationEnum.CALCULATION]}
              </span>
            </div>
          );
        }
        if (val === PriceCalculationEnum.MANUAL_IMPORT) {
          return (
            <div className="d-flex align-items-center">
              <span className="text-primary fw-semibold">
                {PriceCalculationLabels[PriceCalculationEnum.MANUAL_IMPORT]}
              </span>
            </div>
          );
        }
        return (
          <div className="d-flex align-items-center">
            <span className="text-danger fw-semibold">
              {PriceCalculationLabels[PriceCalculationEnum.NOT_SET]}
            </span>
          </div>
        );
      },
    },
    ...(!isReorderMode && (USER_PERMISSIONS?.MODIFY || USER_PERMISSIONS?.REMOVE)
      ? [
          {
            title: "Action",
            key: "action",
            render: (_: unknown, record: any) => (
              <div
                className="d-flex justify-content-center"
                onClick={(e) => e.stopPropagation()}
              >
                <Dropdown
                  overlay={() => MoreOptionsMenu(record)}
                  trigger={["click"]}
                  placement="bottomRight"
                  className="cursor-pointer"
                  overlayClassName="moreOptionDropdown"
                >
                  <span className="svg-32 svg-outline-primary" role="button">
                    {svg?.tableMoreOption}
                  </span>
                </Dropdown>
              </div>
            ),
            width: 10,
          },
        ]
      : []),
  ];

  const tableComponents = React.useMemo(() => {
    return isReorderMode
      ? {
          body: {
            row: Row,
          },
        }
      : undefined;
  }, [isReorderMode]);

  const handleDeleteConfirm = () => {
    if (!idToDelete) return;

    setDeleting(true);

    dispatch(
      deleteProductTypeAPI({
        id: idToDelete,
        onSuccess: () => {
          setDeleting(false);
          setIdToDelete(null);
          setCurrentPage(1);
          fetchProductTypes(1, true);
        },
        onError: () => {
          setDeleting(false);
        },
      }),
    );
  };

  const handleCloseDeleteModal = () => {
    setIdToDelete(null);
  };

  return USER_PERMISSIONS?.READ ? (
    <>
      <div className="page-content h-100 px-40 py-40 d-flex flex-column">
        <div className="flex-shrink-0">
          {isReorderMode ? (
            <div className="mb-sm-4 mb-3 gap-2 d-flex flex-wrap justify-content-sm-between justify-content-center align-items-center">
              <div className="d-flex align-items-center gap-sm-3 gap-2 mob-w-100">
                <span
                  className="cursor-pointer svg-24"
                  onClick={() => {
                    setIsReorderMode(false);
                    setReorderItemsList([]);
                  }}
                >
                  {svg?.backArrow || "←"}
                </span>
                <h2 className="text-black fs-36 fw-bolder page-title mb-0">
                  Reorder Types
                </h2>
              </div>
              <div className="row mob-w-100">
                <div className="col-sm-auto col-6">
                  <button
                    className="btn btn-outline-secondary px-4 fw-bold w-100"
                    onClick={() => {
                      setIsReorderMode(false);
                      setReorderItemsList([]);
                    }}
                    disabled={reordering}
                  >
                    CLOSE
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-sm-4 mb-3 d-flex flex-lg-nowrap flex-wrap gap-md-3 gap-2 justify-content-between align-items-center">
              <h2 className="text-black fs-36 fw-bolder page-title mb-0 order-sm-1 order-1">
                Product Types {totalCount > 0 && `(${totalCount})`}
              </h2>
              <div className="pageSearch mw-100 position-relative ms-sm-auto order-sm-2 order-3 w-mob-65">
                <input
                  type="text"
                  value={search}
                  className="form-control form-control-lg"
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search"
                />
                <span className="cursor-pointer search-icon">
                  {search.length ? (
                    <span onClick={() => setSearch("")}>
                      {svg?.searchClear}
                    </span>
                  ) : (
                    svg?.search
                  )}
                </span>
              </div>
              {USER_PERMISSIONS?.ADD && (
                <button
                  className="btn btn-primary btn-lg mob-btn px-md-4 ms-sm-0 ms-auto order-sm-3 order-4"
                  onClick={() => {
                    setSelectedProductType(null);
                    setIsCreateModalOpen(true);
                  }}
                >
                  + CREATE
                </button>
              )}
              <div className="d-flex gap-md-3 gap-2 order-md-4 order-sm-3 order-2">
                {USER_PERMISSIONS?.MODIFY && productTypes.length > 0 && (
                  <Dropdown
                    overlay={headerMoreMenu}
                    trigger={["click"]}
                    placement="bottomRight"
                  >
                    <span className="cursor-pointer svg-46">
                      {svg?.tableMoreOption}
                    </span>
                  </Dropdown>
                )}
                <NotificationButton />
              </div>
            </div>
          )}
        </div>
        <div
          className="card page-table-card page-table-device border-0 shadow rounded-4 flex-fill overflow-y-auto"
          style={{ minHeight: 0 }}
        >
          <div className="card-body overflow-y-auto p-0" id="scrollableDiv">
            {loading ? (
              <FullScreenLoader />
            ) : productTypes?.length === 0 ? (
              <NoDataMessage
                icon={svg?.noProductTypes}
                text="You do not have any Product Types yet."
              />
            ) : (
              <InfiniteScroll
                dataLength={productTypes.length}
                next={handleLoadMore}
                hasMore={hasMore}
                loader={
                  <div className="text-center p-2">
                    <Spin />
                  </div>
                }
                scrollableTarget="scrollableDiv"
                height={"100%"}
              >
                {isReorderMode ? (
                  <DndContext
                    sensors={sensors}
                    modifiers={[restrictToVerticalAxis]}
                    onDragEnd={onDragEnd}
                  >
                    <SortableContext
                      items={productTypes.map((i) => i._id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <Table
                        dataSource={productTypes}
                        scroll={{ x: "max-content" }}
                        pagination={false}
                        columns={columns}
                        onChange={handleTableChange}
                        rowKey={(record) => record._id}
                        className="custom-table"
                        bordered
                        components={tableComponents}
                      />
                    </SortableContext>
                  </DndContext>
                ) : !isMobile ? (
                  <Table
                    dataSource={productTypes}
                    scroll={{ x: "max-content" }}
                    pagination={false}
                    columns={columns}
                    onChange={handleTableChange}
                    rowKey={(record) => record._id}
                    className="custom-table"
                    bordered
                    components={tableComponents}
                  />
                ) : (
                  <div className="px-3">
                    <div className="row gx-3 gy-3">
                      {productTypes?.map((record: any, index: number) => {
                        let priceCalcVal = record.price_calculation;
                        if (priceCalcVal === true)
                          priceCalcVal = PriceCalculationEnum.CALCULATION;
                        if (priceCalcVal === false || !priceCalcVal)
                          priceCalcVal = PriceCalculationEnum.NOT_SET;

                        return (
                          <div className="col-md-6" key={record._id || index}>
                            <div className="card mobile-table-card mobile-font-s rounded-4 p-3 h-100">
                              {/* Header: Name & Action Dropdown */}
                              <div className="row align-items-start mb-2">
                                <div className="col-sm-5 col-5 text-secondary fw-medium">
                                  Name:
                                </div>
                                <div className="col-sm-7 col-7 d-flex justify-content-between gap-2">
                                  <span
                                    className="fw-bold fs-5 text-break text-primary"
                                    style={{
                                      minWidth: 0,
                                      wordBreak: "break-word",
                                      overflowWrap: "anywhere",
                                      whiteSpace: "normal",
                                    }}
                                  >
                                    {record.name || "-"}
                                  </span>

                                  {(USER_PERMISSIONS?.MODIFY ||
                                    (USER_PERMISSIONS?.REMOVE &&
                                      !record?.is_default)) && (
                                    <div
                                      className="flex-shrink-0 ms-2"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Dropdown
                                        overlay={() => MoreOptionsMenu(record)}
                                        trigger={["click"]}
                                        placement="bottomRight"
                                        overlayClassName="moreOptionDropdown"
                                      >
                                        <span className="cursor-pointer d-inline-block">
                                          {svg?.tableMobileOption}
                                        </span>
                                      </Dropdown>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Details Grid */}
                              <div className="mobile-card-details fs-6 fs-6-mob">
                                <div className="row gy-2">
                                  <div className="col-12">
                                    <div className="row">
                                      <div className="col-sm-5 col-5 text-secondary fw-medium">
                                        Additional Field:
                                      </div>
                                      <div className="col-sm-7 col-7">
                                        {record.load_key
                                          ? AdditionalProductFieldsLabels[
                                              record.load_key as AdditionalProductFieldsEnum
                                            ]
                                          : "-"}
                                      </div>
                                    </div>
                                  </div>

                                  <div className="col-12">
                                    <div className="row">
                                      <div className="col-sm-5 col-5 text-secondary fw-medium">
                                        Mark Up:
                                      </div>
                                      <div className="col-sm-7 col-7">
                                        {record.mark_up
                                          ? `${record.mark_up}%`
                                          : "0%"}
                                      </div>
                                    </div>
                                  </div>

                                  <div className="col-12">
                                    <div className="row">
                                      <div className="col-sm-5 col-5 text-secondary fw-medium">
                                        Installation Mark Up:
                                      </div>
                                      <div className="col-sm-7 col-7">
                                        {record.installation_mark_up
                                          ? `${record.installation_mark_up}%`
                                          : "0%"}
                                      </div>
                                    </div>
                                  </div>

                                  <div className="col-12">
                                    <div className="row">
                                      <div className="col-sm-5 col-5 text-secondary fw-medium">
                                        Necessary:
                                      </div>
                                      <div className="col-sm-7 col-7">
                                        {record.quote_product_is_required
                                          ? "Yes"
                                          : "No"}
                                      </div>
                                    </div>
                                  </div>

                                  <div className="col-12">
                                    <div className="row">
                                      <div className="col-sm-5 col-5 text-secondary fw-medium">
                                        Price Calculation:
                                      </div>
                                      <div className="col-sm-7 col-7">
                                        {priceCalcVal ===
                                        PriceCalculationEnum.CALCULATION ? (
                                          <span className="text-success fw-semibold">
                                            {
                                              PriceCalculationLabels[
                                                PriceCalculationEnum.CALCULATION
                                              ]
                                            }
                                          </span>
                                        ) : priceCalcVal ===
                                          PriceCalculationEnum.MANUAL_IMPORT ? (
                                          <span className="text-primary fw-semibold">
                                            {
                                              PriceCalculationLabels[
                                                PriceCalculationEnum
                                                  .MANUAL_IMPORT
                                              ]
                                            }
                                          </span>
                                        ) : (
                                          <span className="text-danger fw-semibold">
                                            {
                                              PriceCalculationLabels[
                                                PriceCalculationEnum.NOT_SET
                                              ]
                                            }
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </InfiniteScroll>
            )}
          </div>
        </div>
      </div>

      {isCreateModalOpen && (
        <CreateProductTypeModal
          open={isCreateModalOpen}
          onClose={() => {
            setSelectedProductType(null);
            setIsCreateModalOpen(false);
          }}
          refreshData={() => {
            setCurrentPage(1);
            fetchProductTypes(1, true);
          }}
          editData={selectedProductType}
        />
      )}

      <CommonConfirmationModal
        open={idToDelete != null}
        title="Remove Product Type, Are You Sure?"
        subTitle="Removing this product type will delete it from the list and it will no longer be available for selection. All products currently using this product type will be marked as inactive. You can update those products with a new product type and reactivate them if needed. Please confirm before proceeding."
        loading={deleting}
        onSuccess={handleDeleteConfirm}
        onClose={handleCloseDeleteModal}
      />
    </>
  ) : authLoading ? (
    <FullScreenLoader />
  ) : (
    <NoPermissionPage />
  );
};

export default ProductTypes;
